import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { ModalFormContainer } from '@components/ModalFormContainer'

import { ACTION_CODE } from '@constants'
import { SelectOption } from '@types'

import { AccordionConfigExamples } from '@gantt/components/ConfigExamples'
import {
  AvailableVariables,
  GANTT_BIND_TYPE_LIST,
  OPEN_MODAL_BIND_TYPE,
  PageContext,
} from '@gantt/components/GanttCreateOrEdit'
import {
  COMMON_META_DATA,
  contextMenuAvailableKeys,
  initialAvailableVariables,
} from '@gantt/constants'
import { checkValidGanttJS, checkValidGanttJSON, getOptionByStr } from '@gantt/helpers'
import { GANTT_SCRIPT_RETURN_TYPE } from '@gantt/types'

import { VariablesList } from '../GanttConfiguration/components/VariablesList'

import { useAddGanttActionTimelinesDialog } from './hooks/useAddGanttActionTimelinesDialog'

const getHints = (actionCode: ACTION_CODE) => {
  return {
    json:
      actionCode === ACTION_CODE.OPEN_CONTEXT_MENU
        ? i18next.t('ganttCreate.timelineForm.hint.contextMenuJsonHint')
        : undefined,
  }
}

const commandPlaceholders = {
  command: i18next.t('placeholder.selectCommand'),
}

const formPlaceholders = {
  form: i18next.t('placeholder.objectFormCode'),
}

export const AddGanttActionTimelinesDialog = () => {
  const {
    showDialog,
    handleCloseModal,
    currentBarObject,
    availableVariables,
    currentActionTimelineList,
    currentConfiguration,
  } = useContext(PageContext)
  const {
    actionInputs,
    isDirty,
    methods,
    handleSave,
    key,
    loading,
    actionValueOptions,
    watchActionCode,
    watchActionFieldBindType,
  } = useAddGanttActionTimelinesDialog()
  const { t } = useTranslation()
  const [isInvalidScript, setIsInvalidScript] = useState(false)

  const bindings: SelectOption[] = useMemo(() => {
    if (watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU) {
      return [
        getOptionByStr(
          OPEN_MODAL_BIND_TYPE.CONTEXT_MENU,
          t('ganttCreate.timelineForm.bindTypeContextMenu')
        ),
      ]
    }

    if (watchActionCode === ACTION_CODE.OPEN_TOOLTIP) {
      return [
        getOptionByStr(OPEN_MODAL_BIND_TYPE.TOOLTIP, t('ganttCreate.timelineForm.bindTypeTooltip')),
      ]
    }

    if (watchActionCode === ACTION_CODE.OPEN_FORM_MODEL) {
      return [
        getOptionByStr(OPEN_MODAL_BIND_TYPE.FORM, t('ganttCreate.timelineForm.bindTypeForm')),
        getOptionByStr(OPEN_MODAL_BIND_TYPE.JSON),
      ]
    }

    if (watchActionCode === ACTION_CODE.EXECUTE_COMMAND) {
      return [getOptionByStr(GANTT_BIND_TYPE_LIST.COMMANDS)]
    }

    return []
  }, [watchActionCode])

  const availableRefObjectCodes = currentConfiguration
    ? [
        currentConfiguration.resource.data?.id ?? '',
        ...currentConfiguration.timeline.flatMap(item => (item.data?.id ? [item.data?.id] : [])),
      ]
    : []

  const handleClose = () => {
    handleCloseModal(isDirty)
  }

  const availableVariablesByCode: AvailableVariables = useMemo(() => {
    if (watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU) {
      return availableVariables
    }

    return { ...initialAvailableVariables(), ...COMMON_META_DATA }
  }, [watchActionCode, availableVariables])

  const funValidator = useMemo(
    () => (value: string, returnValueType: GANTT_SCRIPT_RETURN_TYPE, keys?: string[]) => {
      if (watchActionFieldBindType === GANTT_BIND_TYPE_LIST.JSON) {
        return checkValidGanttJSON(value, returnValueType, keys)
      }

      return checkValidGanttJS(value, availableVariablesByCode, returnValueType, keys)
    },
    [availableVariablesByCode, watchActionFieldBindType]
  )

  const funValidatorContextMenu = useCallback(
    (value: string) => {
      return funValidator(
        value,
        GANTT_SCRIPT_RETURN_TYPE.ARRAY_OF_OBJECTS,
        contextMenuAvailableKeys
      )
    },
    [watchActionFieldBindType]
  )

  const handleAfterValidationScript = useCallback((state: boolean) => {
    setIsInvalidScript(state)
  }, [])

  useEffect(() => {
    handleAfterValidationScript(false)
  }, [watchActionFieldBindType])

  const formInput = useMemo(() => {
    const selectedForms = currentActionTimelineList
      .map(action => {
        if (action.actionField.bindType === GANTT_BIND_TYPE_LIST.FORM)
          return action.actionField.form.id

        return ''
      })
      .filter(item => item)

    return {
      selectedOptions: selectedForms,
    }
  }, [currentActionTimelineList])

  return (
    <ModalWrapper
      btnText={t('ganttCreate.common.apply')}
      disabledSave={!isDirty || isInvalidScript}
      isShow={showDialog}
      width={1100}
      onClose={handleClose}
      onSave={handleSave}
    >
      <ModalFormContainer key={`AddGanttActionTimelinesDialog_${String(key)}`} maxHeight={500}>
        <FormProvider {...methods}>
          <form>
            <FormGenerator inputs={actionInputs} />
            {[ACTION_CODE.EXECUTE_COMMAND].includes(watchActionCode) && (
              <BindTypeInputs
                bindType={GANTT_BIND_TYPE_LIST.COMMANDS}
                bindTypeOptions={bindings}
                containerName='actionField'
                object={currentBarObject}
                placeholderDict={commandPlaceholders}
                valueInputLabel={t('ganttCreate.resourceForm.command')}
                commandPicker={{
                  isTypeHidden: true,
                  canChangeRefObject: true,
                  availableRefObjectCodes,
                }}
              />
            )}
            {[
              ACTION_CODE.OPEN_CONTEXT_MENU,
              ACTION_CODE.OPEN_FORM_MODEL,
              ACTION_CODE.OPEN_TOOLTIP,
            ].includes(watchActionCode) && (
              <>
                <BindTypeInputs
                  bindTypeOptions={bindings}
                  containerName='actionField'
                  formInput={formInput}
                  hintDict={getHints(watchActionCode)}
                  isShownBinding={Boolean(bindings?.length)}
                  placeholderDict={formPlaceholders}
                  valueOptions={actionValueOptions}
                  scriptEditor={{
                    validator: funValidatorContextMenu,
                    onAfterValidation: handleAfterValidationScript,
                  }}
                  valueInputLabel={
                    watchActionCode === ACTION_CODE.OPEN_FORM_MODEL ? t('label.code') : undefined
                  }
                />
                {watchActionFieldBindType &&
                  [GANTT_BIND_TYPE_LIST.JS, GANTT_BIND_TYPE_LIST.JSON].includes(
                    watchActionFieldBindType
                  ) && (
                    <AccordionConfigExamples>
                      <VariablesList variables={availableVariables} />
                    </AccordionConfigExamples>
                  )}
              </>
            )}
          </form>
        </FormProvider>
      </ModalFormContainer>
    </ModalWrapper>
  )
}
